<template>
  <div>
    <TitlePage title="Partite aperte"></TitlePage>

    <v-card tile class="filter-card mb-4">
      <v-toolbar flat dense>
        <v-toolbar-title class="subtitle-2 font-weight-bold">
          {{ $t('filterResults') }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn small color="error" @click="resetFilters">
          <v-icon left small>mdi-restore</v-icon>
          RESET
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-row class="px-4 pt-3 pb-1 align-center">
        <v-col cols="12" md="2">
          <SportsSelectSearch
              v-model="filters.sport_id"
              :multiple="false"
              :returnObject="false"
              :includeAllOption="true"
              @message="(id) => { filters.sport_id = id }"
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-select
              v-model="filters.status"
              :items="statusFilterItems"
              item-text="text"
              item-value="value"
              label="Stato"
              clearable
              dense
              outlined
              hide-details
          />
        </v-col>

        <v-col cols="12" md="2">
          <CenterSelectSearch
              v-model="filters.center_id"
              :multiple="false"
              :includeAllOption="true"
              autoStrategy="none"
              :returnObject="false"
              @message="(id) => { filters.center_id = id }"
          />
        </v-col>

        <v-col cols="12" md="4">
          <DateRangeQuick
              v-model="dateFilter"
              label="Periodo"
              @change="onDateChange"
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-text-field v-model="filters.code" label="Codice" dense hide-details outlined clearable></v-text-field>
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field v-model="filters.organizer" label="Organizzatore" dense hide-details outlined clearable></v-text-field>
        </v-col>

        <v-col cols="12" md="5">
          <v-text-field
              ref="cityInput"
              v-model="cityLabel"
              label="Luogo"
              dense
              hide-details
              outlined
              clearable
              @click:clear="clearGeo"
          />
        </v-col>

        <v-col cols="12" md="2">
          <v-select
              v-model="filters.radius_km"
              :items="[2, 5, 10, 20, 50, 100]"
              label="Raggio (km)"
              dense
              outlined
              hide-details
          />
        </v-col>
      </v-row>
    </v-card>

    <div style="text-align: right;font-size: 11px;font-weight: 600;padding-bottom: 5px;">
      <span>{{ items_count }} risultati trovati</span>
    </div>

    <v-data-table
        :headers="headers"
        :items="items"
        item-key="_id"
        show-expand
        class="elevation-1"
        disable-pagination
        hide-default-footer
    >
      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusMeta(item.status).color" text-color="white">{{ statusMeta(item.status).text }}</v-chip>
      </template>

      <template v-slot:item.code="{ item }">
        <strong>{{ item.code || item.booking_code || '-' }}</strong>
      </template>

      <template v-slot:item.sport="{ item }">
        {{ getSportName(item) }}
      </template>

      <template v-slot:item.start_at="{ item }">
        {{ formatMatchDateTime(item) }}
      </template>

      <template v-slot:item.players="{ item }">
        <v-chip small color="green" text-color="white" class="mr-1">
          {{ item.counters ? item.counters.confirmed : item.num_players_confirmed || 0 }}
        </v-chip>
        <v-chip v-if="hasWaitlistEnabled(item)" small color="amber darken-2" text-color="white">
          {{ item.counters ? item.counters.waitlist : item.num_players_waitlist || 0 }}
        </v-chip>
        <span class="ml-1">/ {{ item.num_players_target }}</span>
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ formatDateTime(item.created_at) }}
      </template>

      <template v-slot:item.action="{ item }">
        <v-btn icon color="primary" @click="openEdit(item)">
          <v-icon>mdi-file-edit</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-4">
          <div class="expanded-participants">
            <div class="expanded-section">
              <div class="participant-section-title participant-section-title--confirmed">Confermati</div>
              <div v-if="participantList(item, 'confirmed').length">
                <div v-for="participant in participantList(item, 'confirmed')" :key="participant._id" class="participant-row">
                  <strong>{{ participant.display_name }}</strong>
                  <span>{{ participant.email || '-' }}</span>
                  <span>{{ participant.phone || '-' }}</span>
                  <span class="participant-date">{{ participantDate(participant, 'confirmed') }}</span>
                  <v-chip x-small>{{ participant.team || 'No team' }}</v-chip>
                  <v-btn x-small color="primary" @click="openParticipantEdit(item, participant)">Modifica</v-btn>
                </div>
              </div>
              <div v-else class="empty-text">Nessun partecipante confermato</div>
            </div>

            <div v-if="hasWaitlistEnabled(item)" class="expanded-section">
              <div class="participant-section-title participant-section-title--waitlist">Waitlist</div>
              <div v-if="participantList(item, 'waitlist').length">
                <div v-for="participant in participantList(item, 'waitlist')" :key="participant._id" class="participant-row">
                  <strong>{{ participant.display_name }}</strong>
                  <span>{{ participant.email || '-' }}</span>
                  <span>{{ participant.phone || '-' }}</span>
                  <span class="participant-date">{{ participantDate(participant, 'waitlist') }}</span>
                  <v-chip x-small>{{ participant.role || 'No ruolo' }}</v-chip>
                  <v-btn x-small color="primary" @click="openParticipantEdit(item, participant)">Modifica</v-btn>
                </div>
              </div>
              <div v-else class="empty-text">Nessun utente in waitlist</div>
            </div>

            <div class="expanded-section">
              <div class="participant-section-title participant-section-title--removed">Rimossi</div>
              <div v-if="participantList(item, 'removed').length">
                <div v-for="participant in participantList(item, 'removed')" :key="participant._id" class="participant-row">
                  <strong>{{ participant.display_name }}</strong>
                  <span>{{ participant.email || '-' }}</span>
                  <span>{{ participant.phone || '-' }}</span>
                  <span class="participant-date">{{ participantDate(participant, 'removed') }}</span>
                  <v-chip x-small>{{ participant.role || 'No ruolo' }}</v-chip>
                  <v-btn x-small color="primary" @click="openParticipantEdit(item, participant)">Modifica</v-btn>
                </div>
              </div>
              <div v-else class="empty-text">Nessun partecipante rimosso</div>
            </div>

            <div class="expanded-section expanded-section--details">
              <div class="participant-section-title participant-section-title--details">Dati rapidi</div>
              <div class="detail-line"><strong>Centro:</strong> {{ item.booking_center_name || '-' }}</div>
              <div class="detail-line"><strong>Campo:</strong> {{ item.booking_field_name || '-' }}</div>
              <div class="detail-line"><strong>Organizzatore:</strong> {{ item.organizer_name || '-' }}</div>
              <div class="detail-line"><strong>Telefono:</strong> {{ item.organizer_phone || '-' }}</div>
              <div class="detail-line"><strong>Email:</strong> {{ item.organizer_email || '-' }}</div>
              <div class="detail-line">
                <strong>Data/Orario:</strong>
                <span v-if="item.date_booking">
                  {{ formatDateShort(item.date_booking) }}
                  <span v-if="item.booking_start_time || item.booking_end_time">
                    &nbsp;{{ item.booking_start_time || '' }}{{ item.booking_end_time ? ' – ' + item.booking_end_time : '' }}
                  </span>
                </span>
                <span v-else>{{ formatMatchDateTime(item) }}</span>
              </div>
            </div>
          </div>
        </td>
      </template>
    </v-data-table>

    <div class="text-center pt-3 pb-3">
      <v-row>
        <v-col cols="12" md="10" style="text-align: end">
          <v-pagination
              v-model="options_pagination.page"
              :length="calculatePagination(items_count, options_pagination.limit)"
              :total-visible="10"
              @input="fetchItems"
          ></v-pagination>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
              v-model="options_pagination.limit"
              :items="items_for_page"
              label="Risultati per pagina"
              dense
              hide-details
              small-chips
              outlined
              @change="onLimitChange"
          />
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="dialog" max-width="1180px" persistent>
      <v-card v-if="editedItem">
        <v-card-title class="edit-header">
          <div>
            <span class="headline">Modifica partita</span>
            <div class="edit-subtitle">
              {{ editedItem.code || editedItem.booking_code || '-' }} · {{ formatMatchDateTime(editedItem) }}
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-layout wrap>
            <v-flex md12 class="pl-2 pr-2">
              <v-card outlined class="edit-section">
                <v-card-title class="subtitle-2 font-weight-bold">Dati partita</v-card-title>
                <v-card-text>
                  <v-layout wrap>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Codice partita" v-model="editedItem.code"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Codice booking" v-model="editedItem.booking_code"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-select label="Stato" v-model="editedItem.status" :items="statusSelectItems" item-text="text" item-value="value"></v-select>
                    </v-flex>

                    <v-flex md4 class="pl-3 pr-3">
                      <SportsSelectSearch
                          v-model="editedItem.sport_id"
                          label="Sport"
                          :multiple="false"
                          :returnObject="false"
                          :includeAllOption="false"
                          :outlined="false"
                          :hide-details="false"
                          :dense="false"
                          @message="(id) => { editedItem.sport_id = id }"
                      />
                    </v-flex>
                    <v-flex md4 class="pl-3 pr-3">
                      <v-text-field label="Sport master" v-model="editedItem.sport_master"></v-text-field>
                    </v-flex>
                    <v-flex md4 class="pl-3 pr-3">
                      <v-select label="Livello" v-model="editedItem.match_level" :items="levelItems" item-text="text" item-value="value"></v-select>
                    </v-flex>

                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Prezzo persona" type="number" v-model.number="editedItem.price_per_person"></v-text-field>
                    </v-flex>

                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Giocatori target" type="number" v-model.number="editedItem.num_players_target"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Età media" type="number" v-model.number="editedItem.average_age"></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn elevation="2" color="primary" @click="saveMatchDetails">
                    Salva dati partita
                    <v-icon right>mdi-content-save</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex md12 class="pl-2 pr-2">
              <v-card outlined class="edit-section">
                <v-card-title class="subtitle-2 font-weight-bold">Date e booking</v-card-title>
                <v-card-text>
                  <v-layout wrap>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Inizio partita" type="datetime-local" v-model="editedItem.start_at_local"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Fine partita" type="datetime-local" v-model="editedItem.end_at_local"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Inizio booking" type="datetime-local" v-model="editedItem.booking_start_at_local"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Fine booking" type="datetime-local" v-model="editedItem.booking_end_at_local"></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn elevation="2" color="primary" @click="saveMatchDates">
                    Salva date
                    <v-icon right>mdi-calendar-check</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex md12 class="pl-2 pr-2">
              <v-card outlined class="edit-section">
                <v-card-title class="subtitle-2 font-weight-bold">Luogo e opzioni</v-card-title>
                <v-card-text>
                  <v-layout wrap>
                    <v-flex md6 class="pl-3 pr-3">
                      <v-text-field label="Centro" v-model="editedItem.booking_center_name"></v-text-field>
                    </v-flex>
                    <v-flex md6 class="pl-3 pr-3">
                      <v-text-field label="Campo" v-model="editedItem.booking_field_name"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-select label="Visibilita" v-model="editedItem.visibility" :items="visibilityItems" item-text="text" item-value="value"></v-select>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-switch label="Ospiti" v-model="editedItem.allow_guests" inset dense></v-switch>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-switch label="Overbooking" v-model="editedItem.allow_overbooking" inset dense></v-switch>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-switch label="Bilanciamento auto" v-model="editedItem.auto_balance_teams" inset dense></v-switch>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Max waitlist" type="number" v-model.number="editedItem.waitlist_max" :disabled="!hasWaitlistEnabled(editedItem)"></v-text-field>
                    </v-flex>
                    <v-flex md12 class="pl-3 pr-3">
                      <v-textarea label="Descrizione" v-model="editedItem.description" rows="3"></v-textarea>
                    </v-flex>
                  </v-layout>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn elevation="2" color="primary" @click="saveMatchSettings">
                    Salva settings
                    <v-icon right>mdi-tune</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex md12 class="pl-2 pr-2">
              <v-card outlined class="edit-section">
                <v-card-title class="subtitle-2 font-weight-bold">Organizzatore</v-card-title>
                <v-card-text>
                  <v-layout wrap>
                    <v-flex md4 class="pl-3 pr-3">
                      <v-text-field label="Nome" v-model="editedItem.organizer_name"></v-text-field>
                    </v-flex>
                    <v-flex md4 class="pl-3 pr-3">
                      <v-text-field label="Telefono" v-model="editedItem.organizer_phone"></v-text-field>
                    </v-flex>
                    <v-flex md4 class="pl-3 pr-3">
                      <v-text-field label="Email" v-model="editedItem.organizer_email"></v-text-field>
                    </v-flex>
                    <v-flex md12 class="pl-3 pr-3">
                      <div class="contact-consents-title">Preferenze di contatto organizzatore</div>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-checkbox v-model="editedItem.organizer_consents.phone" label="Telefono" hide-details dense></v-checkbox>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-checkbox v-model="editedItem.organizer_consents.whatsapp" label="WhatsApp" hide-details dense></v-checkbox>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-checkbox v-model="editedItem.organizer_consents.email" label="Email" hide-details dense></v-checkbox>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-checkbox v-model="editedItem.organizer_consents.sms" label="SMS" hide-details dense></v-checkbox>
                    </v-flex>
                  </v-layout>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn elevation="2" color="primary" @click="saveOrganizer">
                    Salva organizzatore
                    <v-icon right>mdi-account-check</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex md12 class="d-flex mt-4 mb-2">
              <v-chip color="primary" label small text-color="white">Partecipanti</v-chip>
            </v-flex>
            <v-flex md12 class="pl-4 pr-4 mb-4">
              <v-card outlined class="guest-add-card">
                <v-card-title class="subtitle-2 font-weight-bold">Aggiungi ospite</v-card-title>
                <v-card-text>
                  <v-layout wrap>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Nome ospite" v-model="guestDraft.guest_name"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Email ospite" v-model="guestDraft.guest_email"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-text-field label="Telefono ospite" v-model="guestDraft.guest_phone"></v-text-field>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-select label="Squadra" v-model="guestDraft.team" :items="teamItems" item-text="text" item-value="value" clearable></v-select>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-select label="Ruolo" v-model="guestDraft.role" :items="participantRoleItems" item-text="text" item-value="value" clearable></v-select>
                    </v-flex>
                    <v-flex md3 class="pl-3 pr-3">
                      <v-select label="Livello skill" v-model="guestDraft.skill_level" :items="skillLevelItems" item-text="text" item-value="value" clearable></v-select>
                    </v-flex>
                    <v-flex md2 class="pl-3 pr-3">
                      <v-text-field label="Valore skill" type="number" v-model.number="guestDraft.skill_value"></v-text-field>
                    </v-flex>
                    <v-flex md2 class="pl-3 pr-3">
                      <v-text-field label="Eta" type="number" v-model.number="guestDraft.age_at_join"></v-text-field>
                    </v-flex>
                    <v-flex md2 class="pl-3 pr-3">
                      <v-checkbox
                          v-model="guestDraft.force_waitlist"
                          label="Waitlist"
                          :disabled="!hasWaitlistEnabled(editedItem)"
                          hide-details
                          dense
                      ></v-checkbox>
                    </v-flex>
                  </v-layout>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn elevation="2" color="success" :disabled="!canAddGuest" @click="saveGuest">
                    Aggiungi ospite
                    <v-icon right>mdi-account-plus</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>
            <v-flex md12 class="pl-4 pr-4">
              <div class="participant-section-title participant-section-title--confirmed">Confermati</div>
              <v-simple-table dense>
                <thead>
                <tr><th>Nome</th><th>Email</th><th>Data</th><th>Squadra</th><th>Azioni</th></tr>
                </thead>
                <tbody>
                <tr v-for="participant in participantList(editedItem, 'confirmed')" :key="participant._id">
                  <td>{{ participant.display_name }}</td>
                  <td>{{ participant.email || '-' }}</td>
                  <td>{{ participantDate(participant, 'confirmed') }}</td>
                  <td>{{ participant.team || '-' }}</td>
                  <td class="text-right participant-actions">
                    <v-btn x-small color="primary" @click="openParticipantEdit(editedItem, participant)">Modifica</v-btn>
                    <v-btn x-small color="amber darken-2" dark :disabled="!hasWaitlistEnabled(editedItem)" @click="quickParticipantStatus(participant, 'waitlist')">Waitlist</v-btn>
                    <v-btn x-small color="error" @click="quickParticipantStatus(participant, 'removed')">Rimuovi</v-btn>
                  </td>
                </tr>
                <tr v-if="!participantList(editedItem, 'confirmed').length">
                  <td colspan="5" class="empty-text">Nessun partecipante confermato</td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-flex>
            <v-flex v-if="hasWaitlistEnabled(editedItem)" md12 class="pl-4 pr-4 mt-4">
              <div class="participant-section-title participant-section-title--waitlist">Waitlist</div>
              <v-simple-table dense>
                <thead>
                <tr><th>Nome</th><th>Email</th><th>Data</th><th>Ruolo</th><th>Azioni</th></tr>
                </thead>
                <tbody>
                <tr v-for="participant in participantList(editedItem, 'waitlist')" :key="participant._id">
                  <td>{{ participant.display_name }}</td>
                  <td>{{ participant.email || '-' }}</td>
                  <td>{{ participantDate(participant, 'waitlist') }}</td>
                  <td>{{ participant.role || '-' }}</td>
                  <td class="text-right participant-actions">
                    <v-btn x-small color="primary" @click="openParticipantEdit(editedItem, participant)">Modifica</v-btn>
                    <v-btn x-small color="success" @click="quickParticipantStatus(participant, 'confirmed')">Conferma</v-btn>
                    <v-btn x-small color="error" @click="quickParticipantStatus(participant, 'removed')">Rimuovi</v-btn>
                  </td>
                </tr>
                <tr v-if="!participantList(editedItem, 'waitlist').length">
                  <td colspan="5" class="empty-text">Nessun utente in waitlist</td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-flex>
            <v-flex md12 class="pl-4 pr-4 mt-4">
              <div class="participant-section-title participant-section-title--removed">Rimossi</div>
              <v-simple-table dense>
                <thead>
                <tr><th>Nome</th><th>Email</th><th>Data</th><th>Ruolo</th><th>Azioni</th></tr>
                </thead>
                <tbody>
                <tr v-for="participant in participantList(editedItem, 'removed')" :key="participant._id">
                  <td>{{ participant.display_name }}</td>
                  <td>{{ participant.email || '-' }}</td>
                  <td>{{ participantDate(participant, 'removed') }}</td>
                  <td>{{ participant.role || '-' }}</td>
                  <td class="text-right participant-actions">
                    <v-btn x-small color="primary" @click="openParticipantEdit(editedItem, participant)">Modifica</v-btn>
                    <v-btn x-small color="success" @click="quickParticipantStatus(participant, 'confirmed')">Conferma</v-btn>
                    <v-btn x-small color="amber darken-2" dark :disabled="!hasWaitlistEnabled(editedItem)" @click="quickParticipantStatus(participant, 'waitlist')">Waitlist</v-btn>
                  </td>
                </tr>
                <tr v-if="!participantList(editedItem, 'removed').length">
                  <td colspan="5" class="empty-text">Nessun partecipante rimosso</td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-btn elevation="2" @click="closeDialog">
            <v-icon left>mdi-keyboard-backspace</v-icon>
            {{ $t('back') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="participantDialog" max-width="720px" persistent>
      <v-card v-if="participantDraft">
        <v-card-title>
          <span class="headline">Modifica partecipante</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeParticipantDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex md12 class="pl-4 pr-4">
              <strong>{{ participantDraft.display_name }}</strong>
              <div class="empty-text">{{ participantDraft.email || '-' }} - {{ participantDraft.phone || '-' }}</div>
            </v-flex>
            <v-flex md4 class="pl-4 pr-4">
              <v-select label="Stato" v-model="participantDraft.status" :items="participantStatusItemsForDialog" item-text="text" item-value="value"></v-select>
            </v-flex>
            <v-flex md4 class="pl-4 pr-4">
              <v-select label="Squadra" v-model="participantDraft.team" :items="teamItems" item-text="text" item-value="value" clearable></v-select>
            </v-flex>
            <v-flex md4 class="pl-4 pr-4">
              <v-select label="Ruolo" v-model="participantDraft.role" :items="participantRoleItems" item-text="text" item-value="value" clearable></v-select>
            </v-flex>
            <v-flex md4 class="pl-4 pr-4">
              <v-select label="Livello skill" v-model="participantDraft.skill_level" :items="skillLevelItems" item-text="text" item-value="value" clearable></v-select>
            </v-flex>
            <v-flex md4 class="pl-4 pr-4">
              <v-text-field label="Valore skill" type="number" v-model.number="participantDraft.skill_value"></v-text-field>
            </v-flex>
            <v-flex md4 class="pl-4 pr-4">
              <v-text-field label="Età" type="number" v-model.number="participantDraft.age_at_join"></v-text-field>
            </v-flex>
            <template v-if="participantDraft.type === 'guest'">
              <v-flex md4 class="pl-4 pr-4">
                <v-text-field label="Nome ospite" v-model="participantDraft.guest_name"></v-text-field>
              </v-flex>
              <v-flex md4 class="pl-4 pr-4">
                <v-text-field label="Email ospite" v-model="participantDraft.guest_email"></v-text-field>
              </v-flex>
              <v-flex md4 class="pl-4 pr-4">
                <v-text-field label="Telefono ospite" v-model="participantDraft.guest_phone"></v-text-field>
              </v-flex>
            </template>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-btn elevation="2" @click="closeParticipantDialog">
            <v-icon left>mdi-keyboard-backspace</v-icon>
            {{ $t('back') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="2" color="primary" @click="saveParticipant">
            {{ $t('save') }}
            <v-icon right>mdi-content-save</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TitlePage from '@/components/utils/TitlePage'
import { mapActions, mapMutations } from 'vuex'
import SportsSelectSearch from '@/components/filters/SportsSelectSearch.vue'
import CenterSelectSearch from '@/components/filters/CenterSelectSearch.vue'
import DateRangeQuick from '@/components/filters/DateRangeQuick.vue'

export default {
  name: 'OpenMatchesAdmin',
  components: { DateRangeQuick, CenterSelectSearch, SportsSelectSearch, TitlePage },
  data() {
    return {
      search: '',
      dialog: false,
      participantDialog: false,
      editedItem: null,
      participantDraft: null,
      participantMatchId: null,
      guestDraft: {
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        team: null,
        role: null,
        skill_level: null,
        skill_value: null,
        age_at_join: null,
        force_waitlist: false,
      },
      sportConfigs: [],
      filters: {
        date_from: '',
        date_to: '',
        code: '',
        organizer: '',
        status: '',
        sport_id: null,
        center_id: null,
        location: '',
        lat: null,
        lng: null,
        radius_km: 10,
      },
      dateFilter: { from: null, to: null },
      cityLabel: '',
      cityAutocomplete: null,
      filterDebounce: null,
      filterReady: false,
      items: [],
      items_count: 0,
      options_pagination: {
        page: 1,
        limit: 10,
      },
      items_for_page: [10, 20, 25, 50, 75, 100],
      headers: [
        { text: 'Codice', value: 'code' },
        { text: 'Data', value: 'start_at' },
        { text: 'Sport', value: 'sport' },
        { text: 'Centro', value: 'booking_center_name' },
        { text: 'Campo', value: 'booking_field_name' },
        { text: 'Sport master', value: 'sport_master' },
        { text: 'Organizzatore', value: 'organizer_name' },
        { text: 'Status', value: 'status' },
        { text: 'Giocatori', value: 'players', sortable: false },
        { text: 'Creata il', value: 'created_at' },
        { text: this.$t('actions'), value: 'action', sortable: false },
      ],
      statusSelectItems: [
        { text: 'Aperta', value: 'open' },
        { text: 'Completa', value: 'full' },
        { text: "Lista d'attesa aperta", value: 'waiting' },
        { text: 'Terminata', value: 'finished' },
        { text: 'Annullata', value: 'cancelled' },
        { text: 'Non disputata', value: 'archived' },
      ],
      statusFilterItems: [
        { text: 'Aperta', value: 'open' },
        { text: 'Completa', value: 'full' },
        { text: "Lista d'attesa aperta", value: 'waiting' },
        { text: 'Terminata', value: 'finished' },
        { text: 'Annullata', value: 'cancelled' },
        { text: 'Non disputata', value: 'archived' },
      ],
      statusItems: ['open', 'full', 'waiting', 'finished', 'cancelled', 'archived'],
      participantStatusItems: [
        { text: 'Confermato', value: 'confirmed' },
        { text: 'Lista attesa', value: 'waitlist' },
        { text: 'Rifiutato', value: 'declined' },
        { text: 'Rimosso', value: 'removed' },
      ],
      teamItems: [
        { text: 'Squadra A', value: 'A' },
        { text: 'Squadra B', value: 'B' },
      ],
      skillLevelItems: [
        { text: 'Livello basso', value: 'low' },
        { text: 'Livello medio', value: 'medium' },
        { text: 'Livello alto', value: 'high' },
      ],
      levelItems: [
        { text: 'Basso', value: 'low' },
        { text: 'Medio', value: 'medium' },
        { text: 'Alto', value: 'high' },
      ],
      visibilityItems: [
        { text: 'Pubblica', value: 'public' },
        { text: 'Privata', value: 'private' },
      ],
    }
  },
  async created() {
    await Promise.all([this.loadSportConfigs(), this.fetchItems()])
    this.filterReady = true
  },
  mounted() {
    this.initCityAutocomplete()
  },
  beforeDestroy() {
    if (this.cityAutocomplete && window.google) {
      window.google.maps.event.clearInstanceListeners(this.cityAutocomplete)
    }
    if (this.filterDebounce) {
      clearTimeout(this.filterDebounce)
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.scheduleFilterSearch()
      }
    },
    cityLabel(value) {
      this.filters.location = value || ''
      if (!value) {
        this.filters.lat = null
        this.filters.lng = null
      }
    }
  },
  computed: {
    participantRoleItems() {
      const match = this.getParticipantMatch()
      const config = this.getSportConfig(match)
      if (!config || !Array.isArray(config.roles)) return []

      return config.roles
          .filter(role => role && role.active !== false)
          .map(role => ({
            text: role.label || role.name || role.key,
            value: role.key,
          }))
    },
    participantStatusItemsForDialog() {
      if (this.hasWaitlistEnabled(this.getParticipantMatch())) {
        return this.participantStatusItems
      }
      return this.participantStatusItems.filter(item => item.value !== 'waitlist')
    },
    canAddGuest() {
      return !!(this.editedItem && this.guestDraft.guest_name && this.guestDraft.guest_name.trim())
    },
  },
  methods: {
    ...mapMutations({
      setLoading: 'settings/setLoading',
      setNotification: 'settings/setNotification',
    }),
    ...mapActions({
      fetchOpenMatches: 'matches/adminOpen',
      updateMatch: 'matches/adminUpdate',
      updateParticipant: 'matches/adminUpdatePlayer',
      addGuest: 'matches/adminAddGuest',
      fetchSportConfigs: 'sportMasterConfig/index',
    }),
    async loadSportConfigs() {
      const result = await this.fetchSportConfigs({ active: true })
      this.sportConfigs = Array.isArray(result) ? result : []
    },
    async fetchItems() {
      this.setLoading(true)
      const result = await this.fetchOpenMatches(this.buildFilters())
      this.items = result && Array.isArray(result.results) ? result.results : []
      this.items_count = result && typeof result.count === 'number' ? result.count : 0
      this.setLoading(false)
    },
    onDateChange(payload) {
      this.filters.date_from = payload && payload.from ? payload.from : ''
      this.filters.date_to = payload && payload.to ? payload.to : ''
    },
    scheduleFilterSearch() {
      if (!this.filterReady) return
      if (this.filterDebounce) {
        clearTimeout(this.filterDebounce)
      }
      this.filterDebounce = setTimeout(() => {
        this.options_pagination.page = 1
        this.fetchItems()
      }, 350)
    },
    initCityAutocomplete() {
      this.$nextTick(() => {
        if (!window.google || !window.google.maps || !this.$refs.cityInput) return
        const input = this.$refs.cityInput.$el.querySelector('input')
        if (!input) return

        const options = {
          types: ['(cities)'],
          componentRestrictions: { country: 'it' },
        }

        this.cityAutocomplete = new window.google.maps.places.Autocomplete(input, options)
        setTimeout(() => {
          input.setAttribute('placeholder', '')
        }, 100)

        this.cityAutocomplete.addListener('place_changed', () => {
          const place = this.cityAutocomplete.getPlace()
          if (!place.geometry) return

          this.filters.lat = place.geometry.location.lat()
          this.filters.lng = place.geometry.location.lng()
          this.cityLabel = place.formatted_address || place.name
          this.filters.location = this.cityLabel
        })
      })
    },
    clearGeo() {
      this.cityLabel = ''
      this.filters.location = ''
      this.filters.lat = null
      this.filters.lng = null
    },
    buildFilters() {
      const params = Object.keys(this.filters).reduce((acc, key) => {
        if (this.filters[key] !== null && this.filters[key] !== '') {
          acc[key] = this.filters[key]
        }
        return acc
      }, {})
      params.page = this.options_pagination.page
      params.limit = this.options_pagination.limit
      return params
    },
    async resetFilters() {
      this.filterReady = false
      this.filters = {
        date_from: '',
        date_to: '',
        code: '',
        organizer: '',
        status: '',
        sport_id: null,
        center_id: null,
        location: '',
        lat: null,
        lng: null,
        radius_km: 10,
      }
      this.dateFilter = { from: null, to: null }
      this.cityLabel = ''
      this.options_pagination.page = 1
      await this.fetchItems()
      this.filterReady = true
    },
    onLimitChange() {
      this.options_pagination.page = 1
      this.fetchItems()
    },
    calculatePagination(count, limit) {
      const pages = Math.ceil((count || 0) / (limit || 10))
      return pages > 0 ? pages : 1
    },
    openEdit(item) {
      this.editedItem = this.prepareEditableMatch(item)
      this.resetGuestDraft()
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.editedItem = null
      this.resetGuestDraft()
    },
    prepareEditableMatch(item) {
      return {
        ...JSON.parse(JSON.stringify(item)),
        organizer_consents: {
          email: !!(item.organizer_consents && item.organizer_consents.email),
          sms: !!(item.organizer_consents && item.organizer_consents.sms),
          whatsapp: !!(item.organizer_consents && item.organizer_consents.whatsapp),
          phone: !!(item.organizer_consents && item.organizer_consents.phone),
        },
        start_at_local: this.toDateTimeLocal(this.getEffectiveStartAt(item)),
        end_at_local: this.toDateTimeLocal(this.getEffectiveEndAt(item)),
        booking_start_at_local: this.toDateTimeLocal(
          item.date_booking && item.booking_start_time
            ? this.buildDateTimeFromParts(item.date_booking, item.booking_start_time)
            : (item.date_booking || null)
        ),
        booking_end_at_local: this.toDateTimeLocal(
          item.date_booking && item.booking_end_time
            ? this.buildDateTimeFromParts(item.date_booking, item.booking_end_time)
            : (item.date_booking || null)
        ),
      }
    },
    defaultGuestDraft() {
      return {
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        team: null,
        role: null,
        skill_level: null,
        skill_value: null,
        age_at_join: null,
        force_waitlist: false,
      }
    },
    resetGuestDraft() {
      this.guestDraft = this.defaultGuestDraft()
    },
    openParticipantEdit(match, participant) {
      this.participantMatchId = match._id
      this.participantDraft = JSON.parse(JSON.stringify(participant))
      this.participantDialog = true
    },
    closeParticipantDialog() {
      this.participantDialog = false
      this.participantDraft = null
      this.participantMatchId = null
    },
    async saveParticipant() {
      const payload = {
        status: this.participantDraft.status,
        team: this.participantDraft.team || null,
        role: this.participantDraft.role || null,
        skill_level: this.participantDraft.skill_level || null,
        skill_value: this.participantDraft.skill_value,
        age_at_join: this.participantDraft.age_at_join,
        guest_name: this.participantDraft.guest_name,
        guest_email: this.participantDraft.guest_email,
        guest_phone: this.participantDraft.guest_phone,
      }

      this.setLoading(true)
      const result = await this.updateParticipant({
        matchId: this.participantMatchId,
        playerId: this.participantDraft._id,
        item: payload,
      })
      this.setLoading(false)

      if (result && result.success) {
        this.replaceMatch(result.result)
        if (this.editedItem && this.editedItem._id === result.result._id) {
          this.editedItem = this.prepareEditableMatch(result.result)
        }
        this.setNotification({
          active: true,
          color: 'success',
          mode: 'multi-line',
          text: this.$t('updateSuccess'),
        })
        this.closeParticipantDialog()
      } else {
        this.setNotification({
          active: true,
          color: 'error',
          mode: 'multi-line',
          text: result && result.message ? result.message : result,
        })
      }
    },
    async quickParticipantStatus(participant, status) {
      if (status === 'waitlist' && !this.hasWaitlistEnabled(this.editedItem)) return
      this.participantMatchId = this.editedItem ? this.editedItem._id : null
      this.participantDraft = JSON.parse(JSON.stringify(participant))
      this.participantDraft.status = status
      await this.saveParticipant()
    },
    replaceMatch(match) {
      const index = this.items.findIndex(item => item._id === match._id)
      if (index >= 0) {
        this.$set(this.items, index, match)
      }
    },
    async saveMatchPayload(payload, successMessage) {
      this.setLoading(true)
      const result = await this.updateMatch({ id: this.editedItem._id, item: payload })
      this.setLoading(false)

      if (result && result.success) {
        this.replaceMatch(result.result)
        this.editedItem = this.prepareEditableMatch(result.result)
        this.setNotification({
          active: true,
          color: 'success',
          mode: 'multi-line',
          text: successMessage || this.$t('updateSuccess'),
        })
      } else {
        this.setNotification({
          active: true,
          color: 'error',
          mode: 'multi-line',
          text: result && result.message ? result.message : result,
        })
      }
    },
    saveMatchDetails() {
      const payload = {
        code: this.editedItem.code,
        booking_code: this.editedItem.booking_code,
        status: this.editedItem.status,
        sport_id: this.editedItem.sport_id,
        sport_master: this.editedItem.sport_master,
        match_level: this.editedItem.match_level,
        price_per_person: this.editedItem.price_per_person,
        num_players_target: this.editedItem.num_players_target,
        average_age: this.editedItem.average_age,
        booking_center_name: this.editedItem.booking_center_name,
        booking_field_name: this.editedItem.booking_field_name,
      }
      return this.saveMatchPayload(payload, 'Dati partita salvati')
    },
    saveMatchDates() {
      const payload = {
        start_at: this.fromDateTimeLocal(this.editedItem.start_at_local),
        end_at: this.fromDateTimeLocal(this.editedItem.end_at_local),
        date_booking: this.editedItem.booking_start_at_local
          ? this.fromDateInputValue(this.editedItem.booking_start_at_local.slice(0, 10))
          : null,
        booking_start_time: this.editedItem.booking_start_at_local
          ? this.editedItem.booking_start_at_local.slice(11, 16)
          : null,
        booking_end_time: this.editedItem.booking_end_at_local
          ? this.editedItem.booking_end_at_local.slice(11, 16)
          : null,
      }
      return this.saveMatchPayload(payload, 'Date partita salvate')
    },
    saveMatchSettings() {
      const payload = {
        visibility: this.editedItem.visibility,
        allow_guests: this.editedItem.allow_guests,
        allow_overbooking: this.editedItem.allow_overbooking,
        waitlist_max: this.editedItem.waitlist_max,
        auto_balance_teams: this.editedItem.auto_balance_teams,
        description: this.editedItem.description,
      }
      return this.saveMatchPayload(payload, 'Settings partita salvati')
    },
    saveOrganizer() {
      const payload = {
        organizer_name: this.editedItem.organizer_name,
        organizer_phone: this.editedItem.organizer_phone,
        organizer_email: this.editedItem.organizer_email,
        organizer_consents: this.editedItem.organizer_consents,
      }
      return this.saveMatchPayload(payload, 'Organizzatore salvato')
    },
    async saveGuest() {
      if (!this.canAddGuest) return

      const payload = {
        guest_name: this.guestDraft.guest_name.trim(),
        guest_email: this.guestDraft.guest_email ? this.guestDraft.guest_email.trim() : null,
        guest_phone: this.guestDraft.guest_phone ? this.guestDraft.guest_phone.trim() : null,
        team: this.guestDraft.team || null,
        role: this.guestDraft.role || null,
        skill_level: this.guestDraft.skill_level || null,
        skill_value: this.guestDraft.skill_value,
        age_at_join: this.guestDraft.age_at_join,
        force_waitlist: !!this.guestDraft.force_waitlist,
      }

      this.setLoading(true)
      const result = await this.addGuest({ matchId: this.editedItem._id, item: payload })
      this.setLoading(false)

      if (result && result.success) {
        this.replaceMatch(result.result)
        this.editedItem = this.prepareEditableMatch(result.result)
        this.resetGuestDraft()
        this.setNotification({
          active: true,
          color: 'success',
          mode: 'multi-line',
          text: 'Ospite aggiunto',
        })
      } else {
        this.setNotification({
          active: true,
          color: 'error',
          mode: 'multi-line',
          text: result && result.message ? result.message : result,
        })
      }
    },
    participantList(item, key) {
      return item && item.participants && Array.isArray(item.participants[key])
          ? item.participants[key]
          : []
    },
    hasWaitlistEnabled(item) {
      return !!(item && item.allow_overbooking)
    },
    participantDate(participant, status) {
      const value = status === 'confirmed'
          ? (participant.joined_at || participant.status_changed_at)
          : (participant.status_changed_at || participant.joined_at)
      return this.formatDateTime(value)
    },
    getParticipantMatch() {
      if (this.editedItem && this.editedItem._id === this.participantMatchId) {
        return this.editedItem
      }
      return this.items.find(item => item._id === this.participantMatchId) || this.editedItem || null
    },
    getSportConfig(match) {
      if (!match || !match.sport_master) return null
      return this.sportConfigs.find(config => config.sport_master === match.sport_master) || null
    },
    statusMeta(status) {
      const map = {
        open: { text: 'Aperta', color: 'success' },
        full: { text: 'Completa', color: 'primary' },
        waiting: { text: "Lista d'attesa aperta", color: 'amber darken-2' },
        finished: { text: 'Terminata', color: 'blue-grey' },
        cancelled: { text: 'Annullata', color: 'error' },
        archived: { text: 'Non disputata', color: 'grey darken-2' },
      }
      return map[status] || { text: status || '-', color: 'info' }
    },
    getSportName(item) {
      return item && item.sport && item.sport.name
          ? item.sport.name
          : '-'
    },
    formatDateShort(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatMatchDateTime(item) {
      return this.formatDateTime(this.getEffectiveStartAt(item))
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (isNaN(date.getTime())) return '-'
      return date.toLocaleString('it-IT')
    },
    getEffectiveStartAt(item) {
      if (!item) return null
      if (item.date_booking && item.booking_start_time) {
        const bookingStart = this.buildDateTimeFromParts(item.date_booking, item.booking_start_time)
        if (bookingStart) return bookingStart
      }
      if (item.date_booking) return item.date_booking
      if (item.booking_date && item.booking_start_time) {
        const bookingStart = this.buildDateTimeFromParts(item.booking_date, item.booking_start_time)
        if (bookingStart) return bookingStart
      }
      if (item.booking_date) return item.booking_date
      return item.start_at || null
    },
    getEffectiveEndAt(item) {
      if (!item) return null
      const bookingDateBase = item.date_booking || item.booking_date
      if (bookingDateBase && item.booking_end_time) {
        const bookingEnd = this.buildDateTimeFromParts(bookingDateBase, item.booking_end_time)
        if (bookingEnd) return bookingEnd
      }
      return item.end_at || null
    },
    buildDateTimeFromParts(dateValue, timeValue) {
      if (!dateValue || !timeValue) return null
      const baseDate = new Date(dateValue)
      if (isNaN(baseDate.getTime())) return null

      const parts = String(timeValue).split(':')
      const hours = parseInt(parts[0], 10)
      const minutes = parseInt(parts[1] || '0', 10)

      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

      baseDate.setHours(hours, minutes, 0, 0)
      return baseDate.toISOString()
    },
    toDateTimeLocal(value) {
      if (!value) return ''
      const date = new Date(value)
      if (isNaN(date.getTime())) return ''
      const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      return offsetDate.toISOString().slice(0, 16)
    },
    fromDateTimeLocal(value) {
      if (!value) return null
      const date = new Date(value)
      return isNaN(date.getTime()) ? null : date.toISOString()
    },
    toDateInputValue(value) {
      if (!value) return ''
      const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
      if (match) return match[1]

      const date = new Date(value)
      if (isNaN(date.getTime())) return ''

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    fromDateInputValue(value) {
      if (!value) return null
      const date = new Date(`${value}T00:00:00`)
      return isNaN(date.getTime()) ? null : date.toISOString()
    },
  },
}
</script>

<style scoped>
.participant-row {
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1fr 1fr auto auto;
  gap: 8px;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #eeeeee;
  font-size: 13px;
}

.empty-text,
.detail-line {
  font-size: 13px;
  color: #666;
  padding: 4px 0;
}

.participant-date {
  color: #555;
  white-space: nowrap;
}

.participant-actions {
  white-space: nowrap;
  min-width: 260px;
}

.participant-actions .v-btn {
  margin-left: 4px;
}

.edit-header {
  border-bottom: 1px solid #eeeeee;
}

.edit-subtitle {
  color: #666;
  font-size: 13px;
  font-weight: 400;
  margin-top: 4px;
}

.edit-section {
  margin-bottom: 16px;
}

.edit-section ::v-deep .v-card__title {
  padding-bottom: 4px;
}

.contact-consents-title {
  color: #555;
  font-size: 13px;
  font-weight: 700;
  margin-top: 6px;
}

.participant-section-title {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  background: #43a047;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}

.participant-section-title--confirmed {
  background: #43a047;
}

.participant-section-title--waitlist {
  background: #ff9800;
}

.participant-section-title--removed {
  background: #e53935;
}

.participant-section-title--details {
  background: #8a8a8a;
}

.expanded-participants {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.expanded-section {
  width: 100%;
}

.expanded-section--details {
  max-width: 520px;
}
</style>
